import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { useListClientsDirect } from "generated/api/v2Internal/client-lifecycle/client-lifecycle";
import { featureFlags } from "shell/platform/config/featureFlags";
import { isSandboxPath } from "shell/platform/config/paths";
import { INTERNAL_ROLES } from "shell/platform/constants/constants";
import { useLocalStorage } from "shell/platform/hooks/useLocalStorage";
import { useOrganizationAccess } from "shell/platform/hooks/useOrganizationAccess";
import { useRoles } from "shell/platform/hooks/useRoles";

import type { ClientScopingContextValue, UserHierarchyLevel } from "./types";

const ACTIVE_MSA_CLIENT_KEY = "active-msa-client-id";
const ACTIVE_SUB_CLIENT_KEY = "active-sub-client-id";

const ClientScopingContext = createContext<
  ClientScopingContextValue | undefined
>(undefined);

interface ClientScopingProviderProps {
  children: ReactNode;
}

const defaultValue: ClientScopingContextValue = {
  hierarchyLevel: "CLIENT",
  msaClientId: undefined,
  activeMsaClient: undefined,
  activeSubClient: undefined,
  activeSubClientId: undefined,
  clientId: "",
  availableMsaClients: [],
  availableSubClients: [],
  canSwitch: false,
  loading: false,
  onMsaClientChange: () => {},
  onSubClientChange: () => {},
};

const ClientScopingProviderInternal = ({
  children,
}: ClientScopingProviderProps) => {
  const { organization, loading: orgLoading } = useOrganizationAccess();
  const { hasRole } = useRoles();
  const isInternal = hasRole(INTERNAL_ROLES);

  const [storedMsaClientId, setStoredMsaClientId] = useLocalStorage<
    string | null
  >(ACTIVE_MSA_CLIENT_KEY, null);

  const [storedSubClientId, setStoredSubClientId] = useLocalStorage<
    string | null
  >(ACTIVE_SUB_CLIENT_KEY, null);

  const orgClientId = organization?.clientId ?? undefined;
  const needsMsaFallback = !isInternal && !orgClientId && !orgLoading;

  const { data: msaClientsData, isLoading: msaClientsLoading } =
    useListClientsDirect(
      { type: "MSA" },
      { query: { enabled: isInternal || needsMsaFallback } },
    );

  const availableMsaClients = useMemo(
    () =>
      msaClientsData?.status === 200 &&
      Array.isArray(msaClientsData.data.clients)
        ? msaClientsData.data.clients
            .filter(c => Boolean(c.id))
            .map(c => ({ id: c.id as string, name: c.name ?? null }))
        : [],
    [msaClientsData],
  );

  const fallbackMsaClientId = needsMsaFallback
    ? (availableMsaClients[0]?.id ?? undefined)
    : undefined;

  const msaClientId = isInternal
    ? (storedMsaClientId ?? undefined)
    : (orgClientId ?? fallbackMsaClientId);

  const { data: subClientsData, isLoading: subClientsLoading } =
    useListClientsDirect(
      { type: "SUB", parentId: msaClientId ?? "" },
      { query: { enabled: !!msaClientId } },
    );

  const availableSubClients = useMemo(
    () =>
      subClientsData?.status === 200 &&
      Array.isArray(subClientsData.data.clients)
        ? subClientsData.data.clients
            .filter(c => Boolean(c.id))
            .map(c => ({ id: c.id as string, name: c.name ?? null }))
        : [],
    [subClientsData],
  );

  const activeSubClientId = useMemo(() => {
    if (!storedSubClientId || availableSubClients.length === 0) return;
    const isValid = availableSubClients.some(c => c.id === storedSubClientId);
    return isValid ? storedSubClientId : undefined;
  }, [storedSubClientId, availableSubClients]);

  useEffect(() => {
    if (
      availableSubClients.length === 1 &&
      !activeSubClientId &&
      availableSubClients[0].id
    ) {
      setStoredSubClientId(availableSubClients[0].id);
    }
  }, [availableSubClients, activeSubClientId, setStoredSubClientId]);

  const hierarchyLevel: UserHierarchyLevel = useMemo(() => {
    if (isInternal) return "INTERNAL";
    if (availableSubClients.length <= 1) return "SUB_CLIENT";
    return "CLIENT";
  }, [isInternal, availableSubClients.length]);

  const activeSubClient = useMemo(
    () => availableSubClients.find(c => c.id === activeSubClientId),
    [activeSubClientId, availableSubClients],
  );

  const activeMsaClient = useMemo(
    () => availableMsaClients.find(c => c.id === msaClientId),
    [msaClientId, availableMsaClients],
  );

  const clientId = activeSubClientId ?? msaClientId ?? "";

  const loading = orgLoading || msaClientsLoading || subClientsLoading;

  const computedCanSwitch =
    isInternal ||
    (hierarchyLevel === "CLIENT" && availableSubClients.length > 1);

  const canSwitchRef = useRef(computedCanSwitch);
  if (!loading) {
    canSwitchRef.current = computedCanSwitch;
  }
  const canSwitch = loading ? canSwitchRef.current : computedCanSwitch;

  const onMsaClientChange = useCallback(
    (newMsaClientId: string | null) => {
      setStoredMsaClientId(newMsaClientId);
      setStoredSubClientId(null);
    },
    [setStoredMsaClientId, setStoredSubClientId],
  );

  const onSubClientChange = useCallback(
    (subClientId: string | null) => {
      setStoredSubClientId(subClientId);
    },
    [setStoredSubClientId],
  );

  const value: ClientScopingContextValue = useMemo(
    () => ({
      hierarchyLevel,
      msaClientId,
      activeMsaClient,
      activeSubClient,
      activeSubClientId,
      clientId,
      availableMsaClients,
      availableSubClients,
      canSwitch,
      loading,
      onMsaClientChange,
      onSubClientChange,
    }),
    [
      hierarchyLevel,
      msaClientId,
      activeMsaClient,
      activeSubClient,
      activeSubClientId,
      clientId,
      availableMsaClients,
      availableSubClients,
      canSwitch,
      loading,
      onMsaClientChange,
      onSubClientChange,
    ],
  );

  return (
    <ClientScopingContext.Provider value={value}>
      {children}
    </ClientScopingContext.Provider>
  );
};

const ClientScopingProviderExternal = ({
  children,
}: ClientScopingProviderProps) => {
  const { organization, loading } = useOrganizationAccess();
  const organizationClientId = organization?.clientId;
  const organizationName = organization?.name;

  const activeMsaClient = useMemo(
    () =>
      organizationClientId
        ? { id: organizationClientId, name: organizationName ?? null }
        : undefined,
    [organizationClientId, organizationName],
  );

  const value: ClientScopingContextValue = useMemo(
    () => ({
      ...defaultValue,
      clientId: organizationClientId ?? "",
      msaClientId: organizationClientId ?? undefined,
      activeMsaClient,
      loading,
    }),
    [organizationClientId, activeMsaClient, loading],
  );

  return (
    <ClientScopingContext.Provider value={value}>
      {children}
    </ClientScopingContext.Provider>
  );
};

const ClientScopingProviderRoleAware = ({
  children,
}: ClientScopingProviderProps) => {
  const { hasRole } = useRoles();
  const isInternal = hasRole(INTERNAL_ROLES);

  if (!featureFlags.enableClientScoping) {
    return (
      <ClientScopingContext.Provider value={defaultValue}>
        {children}
      </ClientScopingContext.Provider>
    );
  }

  if (!isInternal) {
    return (
      <ClientScopingProviderExternal>{children}</ClientScopingProviderExternal>
    );
  }

  return (
    <ClientScopingProviderInternal>{children}</ClientScopingProviderInternal>
  );
};

export const ClientScopingProvider = ({
  children,
}: ClientScopingProviderProps) => {
  if (isSandboxPath(globalThis.location.pathname)) {
    return (
      <ClientScopingContext.Provider value={defaultValue}>
        {children}
      </ClientScopingContext.Provider>
    );
  }

  return (
    <ClientScopingProviderRoleAware>{children}</ClientScopingProviderRoleAware>
  );
};

export const useClientScoping = (): ClientScopingContextValue => {
  const ctx = useContext(ClientScopingContext);
  if (!ctx) {
    throw new Error(
      "useClientScoping must be used within a ClientScopingProvider",
    );
  }
  return ctx;
};

export const useActiveClientId = (): string => {
  const { clientId } = useClientScoping();
  return clientId;
};
